import { Slot } from "@radix-ui/react-slot";
import { domMax, LazyMotion, m } from "motion/react";
import * as React from "react";
import { cn } from "../../../lib/utils";
import { Ripple, useRippleState } from "../../ripple";
import { StateLayer } from "../../shared/state-layer";
import { TouchTarget } from "../../shared/touch-target";
import { BUTTON_TYPOGRAPHY_TOKENS } from "../button/button-tokens";
import { useSplitButtonContext } from "./split-button-context";
import {
	LEADING_ICON_SIZE_FOR_SIZE,
	SPLIT_BUTTON_RADIUS_SPRING,
	SPLIT_BUTTON_TOKENS,
} from "./split-button-tokens";
import type { SplitButtonLeadingProps } from "./split-button-types";
import { splitButtonVariants } from "./split-button-variants";

const SplitButtonLeadingComponent = React.forwardRef<
	HTMLButtonElement,
	SplitButtonLeadingProps
>(
	(
		{
			className,
			style,
			variant = "filled",
			size = "sm",
			icon,
			children,
			disabled,
			asChild = false,
			onClick,
			onPointerDown,
			onPointerUp,
			onPointerLeave,
			onPointerCancel,
			onFocus,
			onBlur,
			...restProps
		},
		ref,
	) => {
		const context = useSplitButtonContext();
		const actualSize = context?.size ?? size;
		const actualVariant = context?.variant ?? variant;

		const [isHovered, setIsHovered] = React.useState(false);
		const [isFocused, setIsFocused] = React.useState(false);
		const [isPressed, setIsPressed] = React.useState(false);

		const tokens = SPLIT_BUTTON_TOKENS[actualSize];
		const typographyClass =
			BUTTON_TYPOGRAPHY_TOKENS[actualSize] ?? BUTTON_TYPOGRAPHY_TOKENS.sm;

		const needsTouchTarget = actualSize === "xs" || actualSize === "sm";

		const {
			ripples,
			onPointerDown: onRipplePointerDown,
			removeRipple,
		} = useRippleState({ disabled });

		const innerCornerRadius =
			isPressed || isFocused
				? tokens.innerCorner.pressed
				: isHovered
					? tokens.innerCorner.hovered
					: tokens.innerCorner.default;

		// Outer corner is always a full pill (50% of height)
		const outerRadius =
			(tokens.containerHeight * tokens.outerCornerPercent) / 100;

		// Format: TL TR BR BL
		// Left outer corners are fully rounded, right inner corners animate
		const borderRadius = `${outerRadius}px ${innerCornerRadius}px ${innerCornerRadius}px ${outerRadius}px`;

		const handleClick = React.useCallback(
			(e: React.MouseEvent<HTMLButtonElement>) => {
				if (disabled) {
					e.preventDefault();
					return;
				}
				onClick?.(e);
			},
			[disabled, onClick],
		);

		const handlePointerDown = React.useCallback(
			(e: React.PointerEvent<HTMLButtonElement>) => {
				onRipplePointerDown(e);
				setIsPressed(true);
				onPointerDown?.(e);
			},
			[onRipplePointerDown, onPointerDown],
		);

		const handlePointerUp = React.useCallback(
			(e: React.PointerEvent<HTMLButtonElement>) => {
				setIsPressed(false);
				onPointerUp?.(e);
			},
			[onPointerUp],
		);

		const handlePointerEnter = React.useCallback(() => {
			if (!disabled) {
				setIsHovered(true);
			}
		}, [disabled]);

		const handlePointerLeave = React.useCallback(
			(e: React.PointerEvent<HTMLButtonElement>) => {
				setIsHovered(false);
				setIsPressed(false);
				onPointerLeave?.(e);
			},
			[onPointerLeave],
		);

		const handlePointerCancel = React.useCallback(
			(e: React.PointerEvent<HTMLButtonElement>) => {
				setIsPressed(false);
				onPointerCancel?.(e);
			},
			[onPointerCancel],
		);

		const handleFocus = React.useCallback(
			(e: React.FocusEvent<HTMLButtonElement>) => {
				if (!disabled) setIsFocused(true);
				onFocus?.(e);
			},
			[disabled, onFocus],
		);

		const handleBlur = React.useCallback(
			(e: React.FocusEvent<HTMLButtonElement>) => {
				setIsFocused(false);
				onBlur?.(e);
			},
			[onBlur],
		);

		const leadingIconSize = LEADING_ICON_SIZE_FOR_SIZE[actualSize];

		const labelContent = asChild
			? (
					React.Children.only(children) as React.ReactElement<{
						children?: React.ReactNode;
					}>
				).props.children
			: children;

		const innerContent = (
			<>
				{needsTouchTarget && <TouchTarget />}
				<StateLayer
					hovered={isHovered}
					focused={isFocused}
					pressed={isPressed}
				/>
				<Ripple ripples={ripples} onRippleDone={removeRipple} />

				{icon && (
					<span
						className="flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full overflow-hidden [&_.md-icon]:text-[length:inherit]!"
						style={{
							fontSize: leadingIconSize,
							width: leadingIconSize,
							height: leadingIconSize,
						}}
					>
						{icon}
					</span>
				)}

				{labelContent && (
					<span className="inline-flex items-center justify-center gap-[inherit]">
						{labelContent}
					</span>
				)}
			</>
		);

		const containerClassName = cn(
			splitButtonVariants({
				variant: actualVariant,
				size: actualSize,
				disabled,
			}),
			"gap-2",
			typographyClass,
			className,
		);

		const mergedStyle = {
			paddingInlineStart: tokens.leadingPadding.start,
			paddingInlineEnd: tokens.leadingPadding.end,
			borderRadius,
			...(style as React.CSSProperties),
		};

		return (
			<LazyMotion features={domMax} strict>
				{asChild ? (
					<Slot
						ref={ref}
						data-split-button-part="leading"
						aria-disabled={disabled || undefined}
						onClick={handleClick}
						onPointerDown={handlePointerDown}
						onPointerUp={handlePointerUp}
						onPointerEnter={handlePointerEnter}
						onPointerLeave={handlePointerLeave}
						onPointerCancel={handlePointerCancel}
						onFocus={handleFocus}
						onBlur={handleBlur}
						style={mergedStyle}
						className={containerClassName}
						{...(restProps as React.HTMLAttributes<HTMLElement>)}
					>
						{React.cloneElement(
							React.Children.only(children) as React.ReactElement<{
								children?: React.ReactNode;
							}>,
							{ children: innerContent },
						)}
					</Slot>
				) : (
					<m.button
						ref={ref}
						type="button"
						data-split-button-part="leading"
						disabled={disabled}
						onClick={handleClick}
						onPointerDown={handlePointerDown}
						onPointerUp={handlePointerUp}
						onPointerEnter={handlePointerEnter}
						onPointerLeave={handlePointerLeave}
						onPointerCancel={handlePointerCancel}
						onFocus={handleFocus}
						onBlur={handleBlur}
						animate={{ borderRadius }}
						transition={SPLIT_BUTTON_RADIUS_SPRING}
						style={{
							paddingInlineStart: tokens.leadingPadding.start,
							paddingInlineEnd: tokens.leadingPadding.end,
							...style,
						}}
						className={containerClassName}
						{...restProps}
					>
						{innerContent}
					</m.button>
				)}
			</LazyMotion>
		);
	},
);

SplitButtonLeadingComponent.displayName = "SplitButtonLeading";

export const SplitButtonLeading = React.memo(SplitButtonLeadingComponent);

// ─────────────────────────────────────────────────────────────────────────────
// Preset Components
// ─────────────────────────────────────────────────────────────────────────────

export const FilledSplitButtonLeading = React.forwardRef<
	HTMLButtonElement,
	Omit<SplitButtonLeadingProps, "variant">
>((props, ref) => <SplitButtonLeading ref={ref} variant="filled" {...props} />);
FilledSplitButtonLeading.displayName = "FilledSplitButtonLeading";

export const TonalSplitButtonLeading = React.forwardRef<
	HTMLButtonElement,
	Omit<SplitButtonLeadingProps, "variant">
>((props, ref) => <SplitButtonLeading ref={ref} variant="tonal" {...props} />);
TonalSplitButtonLeading.displayName = "TonalSplitButtonLeading";

export const ElevatedSplitButtonLeading = React.forwardRef<
	HTMLButtonElement,
	Omit<SplitButtonLeadingProps, "variant">
>((props, ref) => (
	<SplitButtonLeading ref={ref} variant="elevated" {...props} />
));
ElevatedSplitButtonLeading.displayName = "ElevatedSplitButtonLeading";

export const OutlinedSplitButtonLeading = React.forwardRef<
	HTMLButtonElement,
	Omit<SplitButtonLeadingProps, "variant">
>((props, ref) => (
	<SplitButtonLeading ref={ref} variant="outlined" {...props} />
));
OutlinedSplitButtonLeading.displayName = "OutlinedSplitButtonLeading";
